import React, { useMemo, useCallback, type FC } from 'react';
import type { PriceBoxProps } from '../../price.types';
import { cn } from '../../../../../../utils/cn';
import { Text } from '../../../../../../atoms/text';
import Fragment from '../../../../../../molecules/fragment';
import { DiscountSimple } from '../discount-simple';
import { useFinalPriceValidation } from '../../../../../../utils/use-price.hook';
import styles from '../../final-price.module.css';
import { PriceSizeEnum } from '../../price.enum';

export const FinalPriceSimple: FC<PriceBoxProps> = ({ product, variant, variantSize = PriceSizeEnum.SMALL }) => {
    const { isValid, shouldShowContact, priceData } = useFinalPriceValidation(product);

    const isPriceData = useMemo(
        () => isValid || !shouldShowContact || priceData,
        [isValid, shouldShowContact, priceData]
    );

    const hasPriceRange = useMemo(
        () => priceData?.maximumPrice.value !== priceData?.minimumPrice.value,
        [priceData?.maximumPrice.value, priceData?.minimumPrice.value]
    );

    const sizeClass = useMemo(() => (variantSize === PriceSizeEnum.BIG ? styles.big : styles.small), [variantSize]);

    const layoutClass = useMemo(() => {
        switch (variant) {
            case 'horizontal':
                return styles.horizontal;
            case 'details':
                return styles.details;
            default:
                return styles.mediumText;
        }
    }, [variant]);

    const priceClass = useMemo(() => cn(styles.root, layoutClass), [layoutClass]);

    const containerClass = useMemo(
        () =>
            cn(
                variant === 'horizontal' ? styles.blockHorizontal : styles.blockVertical,
                variant === 'details' && styles.fit,
                sizeClass
            ),
        [variant, sizeClass]
    );

    const rangePriceClass = useMemo(
        () => cn(styles.fontBold, variant === 'details' ? styles.details : styles.textSm),
        [variant]
    );

    const renderDiscount = useCallback(() => {
        if (!priceData?.discount || !priceData?.basePrice) return null;

        return (
            <DiscountSimple
                basePrice={priceData.basePrice}
                price={priceData.price}
                variant={variant}
                product={product}
                variantSize={variantSize}
            />
        );
    }, [priceData, variant, product, variantSize]);

    const renderPriceRange = useCallback(() => {
        if (!hasPriceRange) return null;

        return (
            <Fragment>
                <Text className={rangePriceClass}>
                    <Text>{priceData?.minPrice}</Text>
                    <Text> - </Text>
                    <Text>{priceData?.maxPrice}</Text>
                </Text>

                {renderDiscount()}
            </Fragment>
        );
    }, [hasPriceRange, priceData, rangePriceClass, renderDiscount]);

    const renderSinglePrice = useCallback(() => {
        if (hasPriceRange) return null;

        return (
            <div className={styles.priceWrapper}>
                <Text className={priceClass}>{priceData?.price}</Text>

                {renderDiscount()}
            </div>
        );
    }, [hasPriceRange, priceClass, priceData, renderDiscount]);

    const renderContent = useCallback(() => {
        if (!isPriceData) return null;

        return (
            <Fragment>
                {renderPriceRange()}
                {renderSinglePrice()}
            </Fragment>
        );
    }, [isPriceData, renderPriceRange, renderSinglePrice]);

    return (
        <div className={containerClass}>
            <Text showIf={!isPriceData} className={priceClass}>
                Liên hệ
            </Text>

            {renderContent()}
        </div>
    );
};
